<template>
  <div :class="mapScreenShotRequested ? 'HiddenMapDrawView' : 'MapDrawView'">
    <b-message
      v-if="!etablissement || !etablissement.latitude || !etablissement.longitude"
      type="is-warning"
      >{{ $t("mapDraw.no-coordinates") }}
    </b-message>

    <b-message v-if="validationActivated && !innerZoneGeographique" type="is-danger"
      >{{ $t("mapDraw.mandatory-zone-geographique") }}
    </b-message>

    <SelectWithValidation
      v-model="tileChoice"
      :label="$t('mapDraw.tile-choices.label')"
      :data="tileChoicesLabels"
      :select-labels="tileChoicesLabels"
      :validation-activated="false"
      :show-validation-errors="false"
      inline
      vid="selectMapTile"
      class="mb-3 map-choice-label"
    />

    <!-- eslint-disable vue/no-v-html -->
    <div
      v-if="validationActivated"
      class="inputNotice mb-3"
      v-html="$t('projet.en-bref.info-site-aire-educative')"
    ></div>
    <!-- eslint-enable vue/no-v-html -->

    <div v-if="mapVisible" class="MapDrawView-container">
      <!-- Fallback: div natif pour Leaflet si vue-leaflet ne fonctionne pas -->
      <div ref="nativeMap" class="MapDrawView-map native-leaflet-map" style="display: none"></div>

      <l-map
        ref="map"
        class="MapDrawView-map"
        :zoom="initialZoom"
        :center="initialCenter"
        :max-zoom="19"
        :min-zoom="2"
      >
      </l-map>
    </div>

    <div class="MapDrawView-legend-container">
      <div class="MapDrawView-legend">
        <b-icon icon="map-marker-alt" />
        {{ $t("mapDraw.legends.position-ecole") }}
      </div>
      <div class="MapDrawView-legend">
        <b-icon icon="plus" />
        {{ $t("mapDraw.zoomin") }}
      </div>
      <div class="MapDrawView-legend">
        <b-icon icon="minus" />
        {{ $t("mapDraw.zoomout") }}
      </div>
      <div v-if="!readonly" class="MapDrawView-legend">
        <img src="@/assets/images/icones/polygon.svg" class="icon" alt="Polygone" />
        {{ $t("mapDraw.legends.draw-polygon") }}
      </div>
      <div v-if="!readonly" class="MapDrawView-legend">
        <b-icon icon="edit" />
        {{ $t("mapDraw.legends.edit") }}
      </div>
      <div v-if="!readonly" class="MapDrawView-legend">
        <b-icon icon="trash-alt" />
        {{ $t("mapDraw.legends.delete") }}
      </div>

      <!-- eslint-disable vue/no-v-html -->
      <div class="inputNotice mt-3" v-html="$t('mapDraw.legends.help')" />
      <!-- eslint-enable vue/no-v-html -->
    </div>
  </div>
</template>

<script lang="ts">
// L'initialisation de L, leaflet-draw et leurs CSS est maintenant gérée globalement dans main.ts
import L, { FeatureGroup, Icon, LatLng, latLng } from "leaflet"

import { InfoReportingService } from "@/services/log/InfoReportingService"
import { t } from "@/utils/i18n-utils"
import { LMap } from "@vue-leaflet/vue-leaflet"
import { BIcon, BMessage } from "buefy"
import html2canvas from "html2canvas"
import leafletImage from "leaflet-image"
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png"
import markerIcon from "leaflet/dist/images/marker-icon.png"
import markerShadow from "leaflet/dist/images/marker-shadow.png"
import { defineComponent } from "vue"
import SelectWithValidation from "../inputs/SelectWithValidation.vue"
import {
  applyLeafletLocale,
  boundsFromGeoJSON,
  buildDrawnItems,
  isMetropolitanFrance,
  layerToGeoJSONString,
} from "./map-draw-common"

type D = Icon.Default & {
  _getIconUrl?: string
}
delete (Icon.Default.prototype as D)._getIconUrl
Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
})

const TILE_URLS = {
  OPEN_STREET_MAP: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
  SATELLITE:
    "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
}

const TILE_CHOICES = {
  AUTO: t("mapDraw.tile-choices.auto"),
  SATELLITE: t("mapDraw.tile-choices.pref-satellite"),
  OSM: t("mapDraw.tile-choices.pref-osm"),
}

export default defineComponent({
  name: "MapDrawView",
  components: {
    LMap,
    SelectWithValidation,
    BMessage,
    BIcon,
  },
  props: {
    zoneGeographique: {
      type: String,
      required: false,
      default: "",
    },
    etablissement: {
      type: Object,
      required: false,
      default: null,
    },
    mapHasFocus: {
      type: Boolean,
      required: false,
    },
    group: {
      type: String,
      required: false,
      default: "",
    },
    readonly: {
      type: Boolean,
      required: false,
    },
    mapScreenShotRequested: {
      type: Boolean,
      required: false,
    },
    validationActivated: {
      type: Boolean,
      required: false,
      default: false,
    },
    showValidationErrors: {
      type: Boolean,
      required: false,
      default: true,
    },
  },

  emits: ["zone-update", "map-screenshot-done"],
  data() {
    return {
      mapVisible: true,
      infoReportingService: InfoReportingService.INSTANCE,
      markerLocation: null as LatLng | null,
      drawControl: null as any,
      drawControlEditOnly: null as any,
      // MIGRATION Vue2-Leaflet -> @vue-leaflet/vue-leaflet : Utilisation de L au lieu de window.L
      drawnItems: new L.FeatureGroup() as FeatureGroup,
      currentZoom: 5,
      tileURL: TILE_URLS.SATELLITE,
      tileChoicesLabels: Object.values(TILE_CHOICES),
      tileChoice: TILE_CHOICES.AUTO,
      innerZoneGeographique: "",
      mapReadyRetryCount: 0,
      maxMapReadyRetries: 25,
      nativeMapCreated: false,
      currentTileLayer: null as any, // Référence vers la couche de tuiles active
      currentMap: null as any, // Référence vers la carte native Leaflet
    }
  },
  computed: {
    initialCenter(): [number, number] {
      if (this.markerLocation) {
        return [this.markerLocation.lat, this.markerLocation.lng]
      }
      return [46.227, 2.213] // Centre de la France par défaut
    },

    initialZoom(): number {
      return this.markerLocation ? 6 : 5
    },
  },
  watch: {
    mapHasFocus(newFocus: boolean): void {
      this.revealMapOnlyIfNeeded(newFocus)
    },
    zoneGeographique(): void {
      this.innerZoneGeographique = this.zoneGeographique === "null" ? "" : this.zoneGeographique
    },
    etablissement(): void {
      this.setMarkerLocation()
    },
    tileChoice(): void {
      this.updateTileUrl()
      this.updateMapTileLayer(this.currentMap)
    },
  },
  created(): void {
    if (this.zoneGeographique && this.zoneGeographique !== "null") {
      this.innerZoneGeographique = this.zoneGeographique
    }
    this.setMarkerLocation()
    this.revealMapOnlyIfNeeded(this.mapHasFocus)
    // Initialisation correcte de tileChoice
    this.updateTileUrl()
  },
  mounted() {
    // Initialiser la carte manuellement après un court délai
    this.$nextTick(() => {
      setTimeout(() => {
        this.initMap()
      }, 100)
    })
  },
  methods: {
    initMap(): void {
      const nativeMap = this.createNativeLeafletMap()
      //Utilisé pour le changement de vue entre OpenStreetMap et satellite
      this.currentMap = nativeMap

      // Ajoute les différentes couches sur la carte
      this.setMapLayers(nativeMap)

      // Centrage de la carte
      this.centerMap(nativeMap)

      if (this.mapScreenShotRequested) {
        this.createMapScreenshot(nativeMap)
      }
    },

    createNativeLeafletMap(): any {
      if (this.nativeMapCreated && this.currentMap) return this.currentMap

      try {
        const nativeMapElement = this.$refs.nativeMap as HTMLElement
        if (!nativeMapElement) {
          console.error("[MapDrawView] Native map element not found")
          return
        } else {
          nativeMapElement.style.display = "block"
        }

        const vueLeafletMap = this.$el.querySelector(
          ".MapDrawView-map:not(.native-leaflet-map)",
        ) as HTMLElement
        if (vueLeafletMap) {
          vueLeafletMap.style.display = "none"
        }

        const center = this.markerLocation
          ? [this.markerLocation.lat, this.markerLocation.lng]
          : [46.227, 2.213]

        const nativeMap = L.map(nativeMapElement).setView(
          center as [number, number],
          this.currentZoom,
        )

        this.currentMap = nativeMap
        return nativeMap
      } catch (error) {
        // Erreur silencieuse lors de la création de la carte native
        console.error("[MapDrawView] Exception in createNativeLeafletMap:", error)
      }
    },

    /**
     * Centre la carte selon la zone géographique ou le marker, sinon centre sur la France
     */
    centerMap(map: any): void {
      // Nécessite LatLngBounds, latLng, this.innerZoneGeographique, this.markerLocation
      const LatLngBounds = L.LatLngBounds
      const latLng = L.latLng
      const bounds = new LatLngBounds([])
      let hasValidBounds = false
      const padding = 4

      // Ajouter la zone géographique (aire éducative) aux bounds si elle existe
      if (this.innerZoneGeographique) {
        try {
          const zoneGeoParse = JSON.parse(this.innerZoneGeographique)
          if (zoneGeoParse.geometry && zoneGeoParse.geometry.coordinates) {
            zoneGeoParse.geometry.coordinates.forEach((geoCoord: any) => {
              for (let i = 0; i < geoCoord.length; i++) {
                bounds.extend([geoCoord[i][1], geoCoord[i][0]])
                hasValidBounds = true
              }
            })
          }
        } catch (e) {
          // Erreur de parsing, on continue
        }
      }

      // Ajouter TOUJOURS le marker (établissement) aux bounds s'il existe
      // Règle de gestion : afficher l'établissement ET l'aire éducative ensemble
      if (this.markerLocation && this.markerLocation.lat !== 0 && this.markerLocation.lng !== 0) {
        bounds.extend(this.markerLocation)
        hasValidBounds = true
      }

      if (hasValidBounds && bounds.isValid()) {
        map.fitBounds(bounds, { padding: [padding, padding] })
      } else {
        map.panTo(latLng(46.227, 2.213), 2)
      }
    },

    setMapLayers(nativeMap: any): void {
      // TILE LAYER
      //FIX Anomalie #19064 - Fond de carte qui disparait
      const isSatellite = this.tileURL === TILE_URLS.SATELLITE
      // maxNativeZoom adaptatif : 18 pour métropole, 16 pour DOM-TOM et autres régions
      const isMetropole = isMetropolitanFrance(
        this.markerLocation?.lat ?? null,
        this.markerLocation?.lng ?? null,
      )
      const satelliteMaxNativeZoom = isMetropole ? 18 : 16
      this.currentTileLayer = L.tileLayer(this.tileURL, {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        maxNativeZoom: isSatellite ? satelliteMaxNativeZoom : 19,
        maxZoom: 19,
      })
      this.currentTileLayer.addTo(nativeMap)

      // MARKER
      if (this.markerLocation) {
        const marker = L.marker([this.markerLocation.lat, this.markerLocation.lng]).addTo(nativeMap)
        if (this.etablissement?.nom) marker.bindPopup(`<h1>${this.etablissement.nom}</h1>`)
      }

      // Traduit les libéllés de Leaflet Draw
      applyLeafletLocale((k: string) => this.$t(k).toString())

      // Ajout de la zone géographique existante
      this.addDrawnItemsToLayer(nativeMap)

      // Ajout des contrôles de dessin
      this.addDrawnControlsToMap(nativeMap)

      const b = this.innerZoneGeographique
        ? (() => {
            try {
              return boundsFromGeoJSON(JSON.parse(this.innerZoneGeographique))
            } catch {
              return null
            }
          })()
        : null

      if (b) nativeMap.fitBounds(b, { padding: [4, 4] })
      this.nativeMapCreated = true
    },

    setMarkerLocation(): void {
      if (this.etablissement && this.etablissement.latitude && this.etablissement.longitude) {
        this.markerLocation = latLng(
          parseFloat(this.etablissement.latitude),
          parseFloat(this.etablissement.longitude),
        )
      } else {
        this.markerLocation = null
      }
    },

    onZoneChanged(): void {
      this.$emit("zone-update", this.innerZoneGeographique)
    },

    updateZoom(map: any) {
      this.currentZoom = map.getZoom()
      this.updateTileUrl()
      // Mettre à jour la couche de tuiles si on est en mode automatique
      if (this.tileChoice === TILE_CHOICES.AUTO) {
        this.updateMapTileLayer(map)
      }
    },

    updateTileUrl(): void {
      if (this.tileChoice === TILE_CHOICES.SATELLITE) {
        this.tileURL = TILE_URLS.SATELLITE
      } else if (this.tileChoice === TILE_CHOICES.OSM) {
        this.tileURL = TILE_URLS.OPEN_STREET_MAP
      } else {
        // Mode AUTO : streetView (OSM) si zoom < 14, satellite si zoom >= 14
        this.tileURL = this.currentZoom < 14 ? TILE_URLS.OPEN_STREET_MAP : TILE_URLS.SATELLITE
      }
    },

    updateMapTileLayer(map: any): void {
      if (map && this.currentTileLayer) {
        // Supprimer l'ancienne couche de tuiles
        map.removeLayer(this.currentTileLayer)
        // Créer et ajouter la nouvelle couche
        //FIX Anomalie #19064 - Fond de carte qui disparait
        const isSatellite = this.tileURL === TILE_URLS.SATELLITE
        // maxNativeZoom adaptatif : 18 pour métropole, 16 pour DOM-TOM et autres régions
        const isMetropole = isMetropolitanFrance(
          this.markerLocation?.lat ?? null,
          this.markerLocation?.lng ?? null,
        )
        const satelliteMaxNativeZoom = isMetropole ? 18 : 16
        this.currentTileLayer = L.tileLayer(this.tileURL, {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          maxNativeZoom: isSatellite ? satelliteMaxNativeZoom : 19,
          maxZoom: 19,
        })
        this.currentTileLayer.addTo(map)
      }
    },

    updateZoneGeographique(e: any, map: any): void {
      const layers = e.target._layers
      Object.values(layers).forEach((element: any) => {
        if (element._latlngs && element._latlngs.length !== 0) {
          const json = layerToGeoJSONString(element)
          if (!json) return
          this.innerZoneGeographique = json
          element
            .bindTooltip(
              this.$t("mapDraw.aire-educative-tooltip").toString() +
                (this.etablissement?.nom || ""),
              { sticky: true },
            )
            .addTo(map)
          this.onZoneChanged()
        }
      })
    },

    getEditControl(_map: any): any {
      return new (L as any).Control.Draw({
        edit: {
          featureGroup: this.drawnItems,
          remove: false, // Désactiver le bouton de suppression par défaut
        },
        draw: false,
      })
    },

    refreshDrawnItems(map: any): void {
      map.removeLayer(this.drawnItems)
      this.drawnItems = new L.FeatureGroup()
      map.addLayer(this.drawnItems)
    },

    /**
     * This method purpose is to enable the "only one polygon drawn" feature
     */
    enableEditPolygon(e: any, map: any): void {
      // When a polygon has been drawn we disabled the draw control and enable the edit control
      this.drawnItems.addLayer(e.layer)
      map.removeControl(this.drawControl)
      // the drawnItems have changed when removing the previous one
      this.refreshDrawnControlEditOnly(map)
    },

    refreshDrawnControlEditOnly(map: any): void {
      this.drawControlEditOnly = this.getEditControl(map)
      map.addControl(this.drawControlEditOnly)
    },

    revealMapOnlyIfNeeded(reveal: boolean): void {
      if (!this.mapVisible && reveal) {
        this.$nextTick(() => {
          this.mapVisible = true
        })
      }
    },

    addDrawnItemsToLayer(nativeMap: any): void {
      // zone existante
      if (this.innerZoneGeographique) {
        this.drawnItems = buildDrawnItems(this.innerZoneGeographique)
      } else {
        this.drawnItems = new L.FeatureGroup()
      }

      if (this.etablissement?.nom) {
        try {
          this.drawnItems.bindTooltip(
            this.$t("mapDraw.aire-educative-tooltip").toString() + this.etablissement.nom,
            { sticky: true },
          )
        } catch {
          console.error("[MapDrawView] Error binding tooltip")
        }
      }

      nativeMap.addLayer(this.drawnItems)
    },

    addDrawnControlsToMap(nativeMap: any): void {
      // Attacher les événements de zoom pour la carte native
      nativeMap.on("zoomend", () => {
        this.updateZoom(nativeMap)
      })
      nativeMap.on("zoom", () => {
        this.currentZoom = nativeMap.getZoom()
      })

      // Attacher les événements d'édition de zone géographique'
      if (this.readonly) return

      this.drawControl = new (L as any).Control.Draw({
        position: "topleft",
        draw: {
          polyline: false,
          polygon: !this.innerZoneGeographique,
          rectangle: false,
          circle: false,
          marker: false,
          circlemarker: false,
        },
        edit: false,
      })
      this.drawControlEditOnly = this.getEditControl(nativeMap)
      nativeMap.addControl(this.innerZoneGeographique ? this.drawControlEditOnly : this.drawControl)

      nativeMap.on("draw:created", (ev: any) => {
        this.enableEditPolygon(ev, nativeMap)
        this.updateZoneGeographique(ev, nativeMap)
      })
      nativeMap.on("draw:edited", (ev: any) => {
        this.updateZoneGeographique(ev, nativeMap)
      })

      // Ajouter le bouton de suppression personnalisé
      this.addCustomDeleteButton(nativeMap)
    },

    addCustomDeleteButton(map: any): void {
      // Créer un bouton de suppression personnalisé
      const DeleteControl = L.Control.extend({
        onAdd: () => {
          const deleteBtn = L.DomUtil.create("div", "leaflet-control-delete")
          deleteBtn.innerHTML =
            '<a href="#" class="leaflet-control-delete-interior" title="' +
            this.$t("mapDraw.delete").toString() +
            '"><span aria-hidden="true">🗑️</span></a>'

          L.DomEvent.on(deleteBtn, "click", (e: any) => {
            L.DomEvent.stopPropagation(e)
            L.DomEvent.preventDefault(e)
            this.onDelete(map)
          })

          return deleteBtn
        },

        onRemove: () => {
          // Cleanup if needed
        },
      })

      // Ajouter le contrôle à la carte seulement s'il y a un polygone et qu'on n'est pas en readonly
      if (this.innerZoneGeographique && !this.readonly) {
        map.addControl(new DeleteControl({ position: "topleft" }))
      }
    },

    onDelete(map: any): void {
      this.infoReportingService.dialog(
        this.$t("usersList.confirmation").toString(),
        this.$t("mapDraw.delete-draw").toString(),
        this.$t("mapDraw.delete").toString(),
        () => this.deleteAireAndEnableDrawControl(map),
        "is-warning",
      )
    },

    deleteAireAndEnableDrawControl(map: any): void {
      // Supprimer tous les éléments dessinés
      this.drawnItems.clearLayers()

      // Remettre le contrôle de dessin normal (permettre de dessiner un nouveau polygone)
      if (this.drawControlEditOnly) {
        map.removeControl(this.drawControlEditOnly)
      }
      if (this.drawControl) {
        map.addControl(this.drawControl)
      }

      // Vider la zone géographique
      this.innerZoneGeographique = ""
      this.onZoneChanged()
    },

    createMapScreenshot(map: any): void {
      // Attendre que toutes les tuiles soient chargées avant de capturer
      const waitForTilesToLoad = (): Promise<void> => {
        return new Promise((resolve) => {
          // Vérifier si le tile layer est déjà complètement chargé
          if (
            this.currentTileLayer &&
            this.currentTileLayer.isLoading &&
            this.currentTileLayer.isLoading()
          ) {
            // Attendre l'événement "load" du tile layer
            this.currentTileLayer.once("load", () => {
              // Petit délai supplémentaire pour s'assurer que le rendu est terminé
              setTimeout(() => resolve(), 300)
            })
          } else {
            // Si pas de chargement en cours, attendre quand même un peu
            setTimeout(() => resolve(), 300)
          }
        })
      }

      waitForTilesToLoad().then(() => {
        // Sauvegarder le zoom actuel et dézoomer d'un cran pour la capture (si zoom > 16)
        const originalZoom = map.getZoom()
        const shouldDezoom = originalZoom > 16
        
        if (shouldDezoom) {
          map.setZoom(originalZoom - 1)
        }
        
        // Attendre que les tuiles du nouveau niveau de zoom se chargent
        waitForTilesToLoad().then(() => {
          // Masquer temporairement tous les contrôles et overlays
          const controlContainers = map.getContainer().querySelectorAll(".leaflet-control-container")
          const originalDisplayValues: string[] = []

          controlContainers.forEach((container: Element, index: number) => {
            const htmlElement = container as HTMLElement
            originalDisplayValues[index] = htmlElement.style.display
            htmlElement.style.display = "none"
          })

          // Invalider la taille de la carte pour forcer un rafraîchissement
          map.invalidateSize()

          // Attendre un peu pour que le rendu se stabilise
          setTimeout(() => {
            leafletImage(map, async (err: any) => {
              if (!err) {
                const mapElement = map.getContainer()
                if (!mapElement) {
                  console.error("[MapDrawView] Map container element not found")
                  // Restaurer les contrôles
                  controlContainers.forEach((container: Element, index: number) => {
                    const htmlElement = container as HTMLElement
                    htmlElement.style.display = originalDisplayValues[index]
                  })
                  // Restaurer le zoom original si on l'a modifié
                  if (shouldDezoom) {
                    map.setZoom(originalZoom)
                  }
                  return
                }

                // Align le canvas SVG avec le canvas des tuiles car il est plus grand que le conteneur
                this.alignSvgLayersForCapture(
                  mapElement,
                  mapElement.offsetWidth,
                  mapElement.offsetHeight,
                )

                const vectorCanvas = await html2canvas(mapElement, {
                  useCORS: true,
                  allowTaint: true,
                  scale: 1,
                  logging: false,
                  backgroundColor: "#FFFFFF",
                  ignoreElements: (element) => {
                    // Ignorer les contrôles de zoom et d'édition
                    if (element.classList?.contains("leaflet-control-container")) {
                      return true
                    }
                    if (element.classList?.contains("leaflet-control")) {
                      return true
                    }
                    // Ignorer tous les overlays de chargement
                    if (element.classList?.contains("loading-overlay")) {
                      return true
                    }
                    if (element.classList?.contains("modal")) {
                      return true
                    }
                    if (element.classList?.contains("modal-background")) {
                      return true
                    }
                    // Ignorer les voiles et overlays
                    if (
                      element.classList?.contains("leaflet-tile-pane") &&
                      window.getComputedStyle(element).opacity !== "1"
                    ) {
                      return false // Ne pas ignorer, mais s'assurer qu'il est opaque
                    }
                    // Ignorer les éléments avec position fixed ou absolute qui peuvent être des overlays
                    const style = window.getComputedStyle(element)
                    if (
                      style.position === "fixed" &&
                      element !== mapElement &&
                      !mapElement.contains(element)
                    ) {
                      return true
                    }
                    return false
                  },
                })

                // Restaurer les contrôles
                controlContainers.forEach((container: Element, index: number) => {
                  const htmlElement = container as HTMLElement
                  htmlElement.style.display = originalDisplayValues[index]
                })
                
                // Restaurer le zoom original si on l'a modifié
                if (shouldDezoom) {
                  map.setZoom(originalZoom)
                }
                
                // Créer un nouveau canvas avec le ratio 1:2 (275:550) pour le PDF
                const targetWidth = 550
                const targetHeight = 1100
                const resizedCanvas = document.createElement("canvas")
                resizedCanvas.width = targetWidth
                resizedCanvas.height = targetHeight
                const ctx = resizedCanvas.getContext("2d")
                
                if (ctx) {
                  // Calculer les dimensions pour remplir complètement le canvas (crop)
                  const sourceRatio = vectorCanvas.width / vectorCanvas.height
                  const targetRatio = targetWidth / targetHeight
                  
                  let sourceX = 0
                  let sourceY = 0
                  let sourceWidth = vectorCanvas.width
                  let sourceHeight = vectorCanvas.height
                  
                  if (sourceRatio > targetRatio) {
                    // Image source plus large, recadrer les côtés
                    sourceWidth = vectorCanvas.height * targetRatio
                    sourceX = (vectorCanvas.width - sourceWidth) / 2
                  } else {
                    // Image source plus haute, recadrer le haut et le bas
                    sourceHeight = vectorCanvas.width / targetRatio
                    sourceY = (vectorCanvas.height - sourceHeight) / 2
                  }
                  
                  // Dessiner la partie recadrée de l'image pour remplir tout le canvas
                  ctx.drawImage(
                    vectorCanvas,
                    sourceX, sourceY, sourceWidth, sourceHeight,
                    0, 0, targetWidth, targetHeight
                  )
                }
                
                const dataUrl = resizedCanvas.toDataURL("image/png")
                this.$emit("map-screenshot-done", dataUrl)
              } else {
                // Restaurer les contrôles même en cas d'erreur
                controlContainers.forEach((container: Element, index: number) => {
                  const htmlElement = container as HTMLElement
                  htmlElement.style.display = originalDisplayValues[index]
                })
                // Restaurer le zoom original si on l'a modifié
                if (shouldDezoom) {
                  map.setZoom(originalZoom)
                }
                console.error("[MapDrawView] Error while taking map screenshot #NativeMap")
              }
            })
          }, 200)
        })
      })
    },

    // Aligner les couches pour la capture
    alignSvgLayersForCapture(mapContainer: HTMLElement, width: number, height: number): void {
      const svgElements = mapContainer.querySelectorAll(".leaflet-overlay-pane svg")

      if (svgElements?.length > 0) {
        svgElements.forEach((svg) => {
          const svgEl = svg as SVGElement
          svgEl.style.width = width + "px"
          svgEl.style.height = height + "px"
          svgEl.style.transform = "translate3d(0px, 0px, 0px)"
          svgEl.setAttribute("viewBox", "0 0 " + width + " " + height)
        })
      }
    },
  },
})
</script>

<style lang="scss">
@import "../../../node_modules/bulma/sass/utilities/_all.sass";
@import "@/styles/variables.scss";

.map-choice-label .label {
  font-weight: normal;
}

.MapDrawView {
  .MapDrawView-container {
    display: flex;
    justify-content: center;
  }
}

.HiddenMapDrawView {
  .MapDrawView-map {
    width: 275px;
    height: 450px;
  }
}

.MapDrawView-map {
  &.leaflet-container {
    z-index: 1;
    width: 100%;
    height: 50em;
  }
}

@media screen and (max-width: $fullhd) {
  .MapDrawView-map {
    &.leaflet-container {
      width: 100%;
    }
  }
}

//To remove the tooltip that appears when we want to delete a figure
.MapDrawView-container {
  .leaflet-draw-tooltip-single {
    display: none;
  }
}

.MapDrawView-legend-container {
  margin-top: 1.5rem;
  max-width: 500px;
}

.MapDrawView-legend {
  display: flex;
  align-items: center;
}

.MapDrawView-invisible-input {
  .InputWithValidation-field {
    display: none;
  }
}

.leaflet-control-delete-interior {
  background-color: white;
  border: 2px solid rgba(0, 0, 0, 0.2);
  border-radius: 4px;
  padding: 5px;
  cursor: pointer;
}
</style>
